import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { SLOGAN } from "@/lib/site";

const HERO_IMG =
  "https://images.unsplash.com/photo-1497366412874-3415097a27e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwbW9kZXJuJTIwYXJjaGl0ZWN0dXJlJTIwb2ZmaWNlfGVufDB8fHx8MTc4NzE2MjkxM3ww&ixlib=rb-4.1.0&q=85";

const lines = ["Architects", "of Ambition.", "Builders", "of Legacy."];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden"
      data-testid="hero"
    >
      {/* Background image with parallax */}
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
        <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-30 grayscale" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--mpg-bg)] via-[rgba(9,10,9,0.65)] to-[rgba(9,10,9,0.85)]" />
      </motion.div>

      <motion.div
        style={{ y: textY }}
        className="relative z-10 max-w-[1800px] mx-auto w-full px-8 md:px-16 pb-12 md:pb-16 pt-36 md:pt-40"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="font-mono text-xs md:text-sm uppercase tracking-[0.3em] text-[var(--mpg-text-soft)] mb-6 md:mb-8"
          data-testid="hero-overline"
        >
          Madison Price Group LLC — Holding Company
        </motion.p>

        <h1
          className="font-serif text-[15vw] md:text-[10vw] lg:text-[8vw] leading-[0.86] tracking-tighter"
          data-testid="hero-heading"
        >
          {lines.map((line, i) => (
            <span key={line} className="reveal-line">
              <span
                className={`reveal-inner ${i % 2 === 1 ? "italic font-light pl-[0.5em]" : ""}`}
                style={{ animationDelay: `${0.5 + i * 0.12}s` }}
              >
                {line}
              </span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="mt-8 md:mt-10 flex flex-col md:flex-row md:items-end justify-between gap-8 border-t border-[var(--mpg-border)] pt-6"
        >
          <p className="max-w-md text-base md:text-lg font-light leading-relaxed text-[var(--mpg-text-soft)]">
            A parent company building and backing enduring businesses — today, lending and
            marketing; tomorrow, more.
          </p>
          <a
            href="#portfolio"
            className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-[var(--mpg-text)] group"
            data-testid="hero-cta"
          >
            <span className="link-underline">Explore the group</span>
            <ArrowDown size={16} className="group-hover:translate-y-1 transition-transform duration-300" />
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
}
